import React from 'react';
import {useDispatch} from 'react-redux';
import {WeatherService} from '../api';
import {StationRecords} from '../api/types';
import {
  EMPTY_STATION_RECORDS,
  getStationOnThisDayData,
  pushStationOnThisDay,
} from '../store';
import {Dates} from '../utils';
import {isValidMonthDay, MonthDay, onThisDayKey} from '../utils/Records';
import useApiClient from './useApiClient';
import useParamSelector from './useParamSelector';

// Records change at most once a day (nightly profile recompute), as in
// useStationRecords.
const STALENESS_MIN = 6 * 60;
const RETRY_COOLDOWN_MIN = 5;
/** A cold date reads the station's whole history: say so after this long. */
const ON_THIS_DAY_SLOW_MS = 3000;

/**
 * One calendar date of a station's records (On this day in history, FE #94).
 *
 * Its own store entry per station and date, so it never evicts the full
 * Records & climate payload `useStationRecords` caches per station. Asks for
 * `sections=coverage`, which skips `month_years` / `annual` (~230 KB on a
 * long SYNOP record); the backend adds `on_this_day` whenever a month and day
 * are given.
 *
 * The answer is dispatched to the store, so leaving the screen does not lose a
 * cold read (~20 s until backend#257 pre-warms it). A 204 (no profile) caches
 * the bare `{fetched_at}` marker. `isSlow` turns on after 3 s of loading.
 * `error` is set when the last request for this date failed and nothing is
 * cached for it: the screen then offers a retry (`update`, which skips the
 * cooldown) instead of a skeleton that never resolves.
 */
const useStationOnThisDay = (stationId: number, date: MonthDay) => {
  const {fetch} = useApiClient();
  const dispatch = useDispatch();
  const valid = isValidMonthDay(date);
  const key = onThisDayKey(stationId, date);

  const [pending, setPending] = React.useState<Record<string, true>>({});
  const [slowKey, setSlowKey] = React.useState<string | undefined>();
  const [failed, setFailed] = React.useState<Record<string, true>>({});
  // Keyed by station and date, as in useStationRecords (#108): one date's
  // failure must not veto another's first fetch.
  const lastAttemptAtRef = React.useRef<Record<string, number>>({});

  const data: StationRecords = useParamSelector(getStationOnThisDayData, key);
  const isLoading = Boolean(pending[key]);

  React.useEffect(() => {
    if (!isLoading) {
      return undefined;
    }
    const timer = setTimeout(() => setSlowKey(key), ON_THIS_DAY_SLOW_MS);
    return () => clearTimeout(timer);
  }, [isLoading, key]);

  const isUpdateRequired = React.useCallback(() => {
    if (!valid) {
      return false;
    }
    const isStale =
      !data ||
      data === EMPTY_STATION_RECORDS ||
      !data.fetched_at ||
      Dates.timeDiffUtcWithNowInMin(data.fetched_at) > STALENESS_MIN;
    if (!isStale) {
      return false;
    }
    const lastAttemptAt = lastAttemptAtRef.current[key];
    return !(
      lastAttemptAt !== undefined &&
      Dates.timeDiffUtcWithNowInMin(lastAttemptAt) < RETRY_COOLDOWN_MIN
    );
  }, [data, key, valid]);

  const update = React.useCallback(async () => {
    if (!valid || !stationId || pending[key]) {
      return;
    }
    lastAttemptAtRef.current[key] = Dates.now();
    setPending((current) => ({...current, [key]: true}));
    setFailed(({[key]: _cleared, ...rest}) => rest);
    try {
      const response: StationRecords = await fetch(
        WeatherService.stationRecords(stationId, {
          month: date.month,
          day: date.day,
          sections: 'coverage',
        }),
        {ignore404: true},
      );
      dispatch(
        pushStationOnThisDay({
          key,
          data: {...(response || {}), fetched_at: Dates.now()},
        }),
      );
    } catch (_error) {
      // The cooldown in lastAttemptAtRef prevents a hot retry loop; the
      // screen offers a manual retry instead.
      setFailed((current) => ({...current, [key]: true}));
    } finally {
      setPending(({[key]: _done, ...rest}) => rest);
      setSlowKey((current) => (current === key ? undefined : current));
    }
  }, [valid, stationId, pending, key, fetch, date.month, date.day, dispatch]);

  return React.useMemo(
    () => ({
      data,
      isLoading,
      isSlow: isLoading && slowKey === key,
      error: !isLoading && Boolean(failed[key]),
      isUpdateRequired,
      update,
    }),
    [data, isLoading, slowKey, key, failed, isUpdateRequired, update],
  );
};

export default useStationOnThisDay;
